// ui/card-snap.js: M5 Change #4.
//
// "Card snap" is Balatro's MUST #1, when a card LANDS, it scale-punches
// (1.06× over 80ms, springs back) and a woody-thud sample fires in the
// SAME rAF tick. Coupling matters: synthesis §4.5 calls out that the
// audio and visual must read as one event, not two.
//
// This file is the lightweight glue. It owns:
//   1. createCardSnapDetector(): tracks per-card "first seen" so the
//      snap fires exactly once per landing across renders. Used by the
//      networked + hot-seat views which both call `detect(state)` each
//      frame.
//   2. triggerScalePunch(): canvas-side helper invoked by overlays that
//      already manage their own rAF loop (trick-area uses this on the
//      winner card during the resolution sweep).
//
// What this file does NOT do:
//   • It does not own the audio engine, it emits via audio/events.js
//     (M2 contract). The wired engine listens.
//   • It does not draw the scale punch itself. The canvas-render loop
//     owns transform math; this module just answers "is a card mid-
//     punch right now, and what's its current scale factor?"

import * as audio from '../audio/events.js';
import { springOvershoot } from './easing.js';
import { TIMING } from './easing.js';

/** @typedef {{ card_id: string, seat?: number }} Play */

/**
 * Build a snap detector. Hold the returned object across renders; call
 * `detect(state)` each frame with the freshest game snapshot. The
 * detector fires `audio.emit('card_snap')` and `triggerScalePunch` on
 * each card_id transitioning from "not in trick" to "in trick" i.e.
 * every individual play, including bot plays and the human's own.
 *
 * Why card_id (not seat or trick index)? Hands are dealt unique
 * card_ids across a hand; the same id never lands twice within one
 * trick. Across tricks, ids may repeat but we wipe the seen-set on
 * trick reset (detected by trick_plays going from N>0 → 0).
 */
export function createCardSnapDetector() {
  /** @type {Set<string>} */
  let seen = new Set();
  let lastPlayCount = 0;
  /** @type {Map<string, { startMs: number, durationMs: number, magnitude: number }>} */
  const activePunches = new Map();

  function reset() {
    seen.clear();
    lastPlayCount = 0;
    activePunches.clear();
  }

  /**
   * Process a state snapshot. Returns the card_ids that just landed in
   * this call (caller can render extra effects keyed off this).
   *
   * @param {{ trick_plays?: Array<Play>|null }} state
   * @returns {Array<string>}
   */
  function detect(state) {
    const plays = Array.isArray(state?.trick_plays) ? state.trick_plays : [];
    // Trick reset detection, when count dropped to 0 (or shrank), the
    // hand moved to a fresh trick. Clear the seen-set so card_ids that
    // reappear in the next trick aren't skipped.
    if (plays.length < lastPlayCount) {
      seen.clear();
    }
    lastPlayCount = plays.length;

    const newlyLanded = [];
    for (const p of plays) {
      if (!p || !p.card_id) continue;
      if (seen.has(p.card_id)) continue;
      seen.add(p.card_id);
      newlyLanded.push(p.card_id);
    }
    if (newlyLanded.length > 0) {
      // One audio emit per landing event so listeners can play snaps
      // (one source-node per call). Engine no-ops if sample missing.
      for (const id of newlyLanded) {
        audio.emit('card_snap', { card_id: id });
        startPunch(id);
      }
    }
    return newlyLanded;
  }

  function startPunch(cardId, now) {
    const t0 = typeof now === 'number' ? now : nowMs();
    activePunches.set(cardId, {
      startMs: t0,
      durationMs: TIMING.CARD_SNAP_MS,
      magnitude: 1.06,
    });
  }

  /**
   * Scale factor for a card right now, computed from its punch start.
   * Returns 1 if the card isn't mid-punch. Caller passes nowMs.
   *
   * @param {string} cardId
   * @param {number} [now]
   * @returns {number}
   */
  function scaleFor(cardId, now) {
    const p = activePunches.get(cardId);
    if (!p) return 1;
    const t = ((typeof now === 'number' ? now : nowMs()) - p.startMs) / p.durationMs;
    if (t >= 1) {
      activePunches.delete(cardId);
      return 1;
    }
    if (t <= 0) return 1;
    // Punch shape: rise to magnitude on springOvershoot then settle to 1.
    // springOvershoot peaks ~1.097 at t≈0.57; multiplying by magnitude
    // produces a 1.06× peak (0.06 × 1.097 ≈ 0.066 above 1.0).
    const s = springOvershoot(t);
    return 1 + (p.magnitude - 1) * (1 - Math.abs(2 * t - 1));
  }

  function isActive(cardId) {
    return activePunches.has(cardId);
  }

  return { detect, reset, scaleFor, isActive, startPunch };
}

function nowMs() {
  if (typeof performance !== 'undefined' && performance.now) return performance.now();
  return Date.now();
}
